/**
 * sl-ufq6.6: Budget Limits Settings Panel — Redesigned
 *
 * Admin/Owner configures per-member group budgets and individual exceptions.
 */
import { Button, Input, notify, Skeleton } from '@affine/component';
import { SettingRow } from '@affine/component/setting-components';
import { defaultWorkspaceApiClient } from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import {
  type ChangeEvent,
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

// ─── Styles ──────────────────────────────────────────────────────────────────

const cardStyle: CSSProperties = {
  padding: 16,
  backgroundColor: 'var(--streamlain-background-secondary-color)',
  borderRadius: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const cardHeaderStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const cardTitleStyle: CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--streamlain-text-primary-color)',
};

const cardSubtitleStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--streamlain-text-secondary-color)',
};

const fieldRowStyle: CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
};

const fieldLabelStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--streamlain-text-secondary-color)',
  minWidth: 80,
  flexShrink: 0,
};

const selectStyle: CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'var(--streamlain-border-color)',
  fontSize: 13,
  backgroundColor: 'var(--streamlain-background-primary-color)',
  color: 'var(--streamlain-text-primary-color)',
};

const actionRowStyle: CSSProperties = {
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-end',
  paddingTop: 4,
};

const emptyStateStyle: CSSProperties = {
  textAlign: 'center',
  padding: '20px 16px',
  color: 'var(--streamlain-text-secondary-color)',
  fontSize: 13,
};

const badgeStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: 11,
  fontWeight: 500,
};

const badgeBlockStyle: CSSProperties = {
  ...badgeStyle,
  backgroundColor:
    'var(--streamlain-background-error-color, rgba(239,68,68,0.1))',
  color: 'var(--streamlain-error-color, #ef4444)',
};

const badgeWarnStyle: CSSProperties = {
  ...badgeStyle,
  backgroundColor:
    'var(--streamlain-background-warning-color, rgba(255,152,0,0.1))',
  color: 'var(--streamlain-warning-color, #f59e0b)',
};

// ─── Types ───────────────────────────────────────────────────────────────────

interface GroupLimit {
  groupId: string;
  groupName: string;
  isAutoSync: boolean;
  autoSyncFilter: string | null;
  totalLimitMicro: string | null;
  userLimitMicro: string | null;
  automationLimitMicro: string | null;
  workflowLimitMicro: string | null;
  windowSeconds: number;
  behavior: string;
}

interface Override {
  id: string;
  entityType: string;
  entityId: string;
  limitMicro: string | null;
  windowSeconds: number;
  behavior: string;
}

interface BudgetData {
  groupLimits: GroupLimit[];
  overrides: Override[];
}

interface WorkspaceGroup {
  id: string;
  name: string;
  isAutoSync: boolean;
}

interface OverrideTargetData {
  users: Array<{ id: string; name: string | null; email: string }>;
  automations: Array<{
    id: string;
    blockId: string;
    title: string | null;
    workflowId: string | null;
  }>;
  workflows: Array<{ id: string; name: string }>;
}

type OverrideEntityType = 'user' | 'automation' | 'workflow';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const UNITS = [{ seconds: 3600 }, { seconds: 86400 }, { seconds: 604800 }];

function windowToMultiplier(windowSeconds: number): {
  amount: number;
  unitSeconds: number;
} {
  // Try weeks, days, hours — use the largest unit that divides evenly
  for (let i = UNITS.length - 1; i >= 0; i--) {
    const u = UNITS[i];
    if (windowSeconds >= u.seconds && windowSeconds % u.seconds === 0) {
      return { amount: windowSeconds / u.seconds, unitSeconds: u.seconds };
    }
  }
  // Fallback: hours
  return { amount: Math.round(windowSeconds / 3600), unitSeconds: 3600 };
}

function formatCredits(microStr: string | null, unlimited: string): string {
  if (!microStr) return unlimited;
  const display = Number(BigInt(microStr)) / 10000;
  return display.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function formatWindow(
  windowSeconds: number,
  labels: {
    perHour: string;
    perDay: string;
    perWeek: string;
    every: (options: { amount: string; unit: string }) => string;
    hours: string;
    days: string;
    weeks: string;
  }
): string {
  const { amount, unitSeconds } = windowToMultiplier(windowSeconds);
  const unit = UNITS.find(u => u.seconds === unitSeconds);
  if (!unit) return `${windowSeconds}s`;
  if (amount === 1) {
    // Singular
    return unitSeconds === 3600
      ? labels.perHour
      : unitSeconds === 86400
        ? labels.perDay
        : labels.perWeek;
  }
  const unitLabel =
    unitSeconds === 3600
      ? labels.hours
      : unitSeconds === 86400
        ? labels.days
        : labels.weeks;
  return labels.every({ amount: String(amount), unit: unitLabel });
}

function creditsToMicro(displayCredits: string): string {
  const num = parseFloat(displayCredits.replace(/\./g, '').replace(',', '.'));
  if (isNaN(num) || num < 0) return '0';
  return String(Math.round(num * 10000));
}

const EMPTY_OVERRIDE_TARGETS: OverrideTargetData = {
  users: [],
  automations: [],
  workflows: [],
};

// ─── Component ───────────────────────────────────────────────────────────────

export const BudgetLimitsPanel = ({ isAdmin }: { isAdmin: boolean }) => {
  const t = useI18n();
  const unlimitedLabel =
    t['com.streamlain.settings.workspace.ai.budget.unlimited']();
  const windowLabels = {
    perHour: t['com.streamlain.settings.workspace.ai.budget.per-hour'](),
    perDay: t['com.streamlain.settings.workspace.ai.budget.per-day'](),
    perWeek: t['com.streamlain.settings.workspace.ai.budget.per-week'](),
    every: t['com.streamlain.settings.workspace.ai.budget.every'],
    hours: t['com.streamlain.settings.workspace.ai.budget.hours'](),
    days: t['com.streamlain.settings.workspace.ai.budget.days'](),
    weeks: t['com.streamlain.settings.workspace.ai.budget.weeks'](),
  };
  const workspace = useService(WorkspaceService).workspace;
  const workspaceId = workspace.id;

  const [data, setData] = useState<BudgetData | null>(null);
  const [groups, setGroups] = useState<WorkspaceGroup[]>([]);
  const [overrideTargets, setOverrideTargets] = useState<OverrideTargetData>(
    EMPTY_OVERRIDE_TARGETS
  );
  const [loading, setLoading] = useState(true);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddOverrideForm, setShowAddOverrideForm] = useState(false);

  // Form state
  const [formGroupId, setFormGroupId] = useState('');
  const [formCredits, setFormCredits] = useState('');
  const [formWindowAmount, setFormWindowAmount] = useState(1);
  const [formWindowUnit, setFormWindowUnit] = useState(86400);
  const [formBehavior, setFormBehavior] = useState('block');

  // Individual override form state
  const [overrideEntityType, setOverrideEntityType] =
    useState<OverrideEntityType>('user');
  const [overrideEntityId, setOverrideEntityId] = useState('');
  const [overrideCredits, setOverrideCredits] = useState('');
  const [overrideWindowAmount, setOverrideWindowAmount] = useState(1);
  const [overrideWindowUnit, setOverrideWindowUnit] = useState(86400);
  const [overrideBehavior, setOverrideBehavior] = useState('block');

  const apiBase = `/api/workspace/${workspaceId}/ai/budget-limits`;
  const requestBudget = useCallback(
    (
      path: string,
      init?: RequestInit,
      mode: 'background' | 'foreground' = 'background'
    ) =>
      defaultWorkspaceApiClient.request({
        workspaceId,
        path,
        init,
        category: 'settings',
        key: `${init?.method ?? 'GET'}:${path}`,
        mode,
        priority: mode === 'foreground' ? 'foreground' : 'visible',
      }),
    [workspaceId]
  );

  const fetchData = useCallback(async () => {
    try {
      const [budgetResp, groupResp, targetResp] = await Promise.all([
        requestBudget(apiBase),
        requestBudget(`/api/workspace/${workspaceId}/groups`),
        requestBudget(`${apiBase}/override-targets`),
      ]);
      if (budgetResp.ok) setData(await budgetResp.json());
      if (groupResp.ok) {
        const gData = await groupResp.json();
        setGroups(Array.isArray(gData) ? gData : (gData.groups ?? []));
      }
      if (targetResp.ok) {
        setOverrideTargets(await targetResp.json());
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [apiBase, requestBudget, workspaceId]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const formWindowSeconds = formWindowAmount * formWindowUnit;
  const overrideWindowSeconds = overrideWindowAmount * overrideWindowUnit;

  const overrideOptions = useMemo(() => {
    if (overrideEntityType === 'user') {
      return overrideTargets.users.map(user => ({
        id: user.id,
        label: user.name ? `${user.name} (${user.email})` : user.email,
      }));
    }
    if (overrideEntityType === 'automation') {
      return overrideTargets.automations.map(automation => ({
        id: automation.id,
        label: automation.title || automation.blockId,
      }));
    }
    return overrideTargets.workflows.map(workflow => ({
      id: workflow.id,
      label: workflow.name || workflow.id,
    }));
  }, [overrideEntityType, overrideTargets]);

  useEffect(() => {
    if (
      showAddOverrideForm &&
      !overrideOptions.some(option => option.id === overrideEntityId)
    ) {
      setOverrideEntityId(overrideOptions[0]?.id ?? '');
    }
  }, [overrideEntityId, overrideOptions, showAddOverrideForm]);

  const saveGroupLimit = useCallback(
    async (groupId: string) => {
      const body: Record<string, unknown> = {
        totalLimitMicro: formCredits ? creditsToMicro(formCredits) : null,
        windowSeconds: formWindowAmount * formWindowUnit,
        behavior: formBehavior,
      };

      try {
        const response = await requestBudget(
          `${apiBase}/group/${groupId}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-Idempotency-Key': crypto.randomUUID(),
            },
            body: JSON.stringify(body),
          },
          'foreground'
        );
        if (!response.ok) {
          throw new Error(await response.text());
        }
        setEditingGroupId(null);
        setShowAddForm(false);
        void fetchData();
      } catch (error) {
        notify.error({
          title: t['com.streamlain.settings.workspace.ai.budget.save-error'](),
          message: error instanceof Error ? error.message : undefined,
        });
      }
    },
    [
      apiBase,
      fetchData,
      formCredits,
      formWindowAmount,
      formWindowUnit,
      formBehavior,
      requestBudget,
      t,
    ]
  );

  const deleteGroupLimit = useCallback(
    async (groupId: string) => {
      try {
        const response = await requestBudget(
          `${apiBase}/group/${groupId}`,
          {
            method: 'DELETE',
            headers: { 'X-Idempotency-Key': crypto.randomUUID() },
          },
          'foreground'
        );
        if (!response.ok) throw new Error(await response.text());
        void fetchData();
      } catch (error) {
        notify.error({
          title:
            t['com.streamlain.settings.workspace.ai.budget.remove-error'](),
          message: error instanceof Error ? error.message : undefined,
        });
      }
    },
    [apiBase, fetchData, requestBudget, t]
  );

  const deleteOverride = useCallback(
    async (overrideId: string) => {
      try {
        const response = await requestBudget(
          `${apiBase}/override/${overrideId}`,
          {
            method: 'DELETE',
            headers: { 'X-Idempotency-Key': crypto.randomUUID() },
          },
          'foreground'
        );
        if (!response.ok) throw new Error(await response.text());
        void fetchData();
      } catch (error) {
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.ai.budget.override-remove-error'
            ](),
          message: error instanceof Error ? error.message : undefined,
        });
      }
    },
    [apiBase, fetchData, requestBudget, t]
  );

  const saveOverride = useCallback(async () => {
    if (!overrideEntityId) return;
    try {
      const response = await requestBudget(
        `${apiBase}/override`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-Idempotency-Key': crypto.randomUUID(),
          },
          body: JSON.stringify({
            entityType: overrideEntityType,
            entityId: overrideEntityId,
            limitMicro: overrideCredits
              ? creditsToMicro(overrideCredits)
              : null,
            windowSeconds: overrideWindowSeconds,
            behavior: overrideBehavior,
          }),
        },
        'foreground'
      );
      if (!response.ok) throw new Error(await response.text());
      setShowAddOverrideForm(false);
      setOverrideCredits('');
      setOverrideWindowAmount(1);
      setOverrideWindowUnit(86400);
      setOverrideBehavior('block');
      void fetchData();
    } catch (error) {
      notify.error({
        title:
          t[
            'com.streamlain.settings.workspace.ai.budget.override-save-error'
          ](),
        message: error instanceof Error ? error.message : undefined,
      });
    }
  }, [
    apiBase,
    fetchData,
    overrideBehavior,
    overrideCredits,
    overrideEntityId,
    overrideEntityType,
    overrideWindowSeconds,
    requestBudget,
    t,
  ]);

  const startEdit = useCallback((gl: GroupLimit) => {
    setEditingGroupId(gl.groupId);
    setFormGroupId(gl.groupId);
    setFormCredits(
      (gl.userLimitMicro ?? gl.totalLimitMicro)
        ? String(
            Number(BigInt((gl.userLimitMicro ?? gl.totalLimitMicro)!)) / 10000
          )
        : ''
    );
    const { amount, unitSeconds } = windowToMultiplier(gl.windowSeconds);
    setFormWindowAmount(amount);
    setFormWindowUnit(unitSeconds);
    setFormBehavior(gl.behavior);
  }, []);

  const startAdd = useCallback(() => {
    setShowAddForm(true);
    setEditingGroupId(null);
    setFormGroupId(groups[0]?.id ?? '');
    setFormCredits('');
    setFormWindowAmount(1);
    setFormWindowUnit(86400);
    setFormBehavior('block');
  }, [groups]);

  const startAddOverride = useCallback(() => {
    setShowAddOverrideForm(true);
    setOverrideEntityType('user');
    setOverrideEntityId(overrideTargets.users[0]?.id ?? '');
    setOverrideCredits('');
    setOverrideWindowAmount(1);
    setOverrideWindowUnit(86400);
    setOverrideBehavior('block');
  }, [overrideTargets]);

  const configuredGroupIds = useMemo(
    () => new Set(data?.groupLimits.map(gl => gl.groupId) ?? []),
    [data]
  );
  const availableGroups = useMemo(
    () => groups.filter(g => !configuredGroupIds.has(g.id)),
    [groups, configuredGroupIds]
  );
  const overrideLabelByKey = useMemo(() => {
    const labels = new Map<string, string>();
    for (const user of overrideTargets.users) {
      labels.set(
        `user:${user.id}`,
        user.name ? `${user.name} (${user.email})` : user.email
      );
    }
    for (const automation of overrideTargets.automations) {
      labels.set(
        `automation:${automation.id}`,
        automation.title || automation.blockId
      );
      labels.set(
        `automation:${automation.blockId}`,
        automation.title || automation.blockId
      );
    }
    for (const workflow of overrideTargets.workflows) {
      labels.set(`workflow:${workflow.id}`, workflow.name || workflow.id);
    }
    return labels;
  }, [overrideTargets]);
  const hasOverrideTargets =
    overrideTargets.users.length > 0 ||
    overrideTargets.automations.length > 0 ||
    overrideTargets.workflows.length > 0;

  if (loading) return <Skeleton height={100} />;
  if (!data) return null;

  // ─── Form ────────────────────────────────────────────────────────────────

  const renderForm = (targetGroupId: string) => (
    <div style={cardStyle}>
      {showAddForm && (
        <div style={fieldRowStyle}>
          <span style={fieldLabelStyle}>
            {t['com.streamlain.settings.workspace.ai.budget.group']()}
          </span>
          <select
            value={formGroupId}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              setFormGroupId(e.target.value)
            }
            style={{ ...selectStyle, flex: 1 }}
          >
            {availableGroups.map(g => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={fieldRowStyle}>
        <span style={fieldLabelStyle}>
          {t['com.streamlain.settings.workspace.ai.budget.budget']()}
        </span>
        <Input
          type="number"
          value={formCredits}
          onChange={(v: string) => setFormCredits(v)}
          placeholder={unlimitedLabel}
          style={{ flex: 1 }}
        />
        <span
          style={{
            fontSize: 13,
            color: 'var(--streamlain-text-secondary-color)',
          }}
        >
          {t['com.streamlain.settings.workspace.ai.budget.credits']()}
        </span>
      </div>

      <div style={fieldRowStyle}>
        <span style={fieldLabelStyle}>
          {t['com.streamlain.settings.workspace.ai.budget.window']()}
        </span>
        <Input
          type="number"
          value={String(formWindowAmount)}
          onChange={(v: string) =>
            setFormWindowAmount(Math.max(1, parseInt(v) || 1))
          }
          style={{ width: 64 }}
        />
        <select
          value={formWindowUnit}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            setFormWindowUnit(Number(e.target.value))
          }
          style={selectStyle}
        >
          {UNITS.map(u => (
            <option key={u.seconds} value={u.seconds}>
              {u.seconds === 3600
                ? windowLabels.hours
                : u.seconds === 86400
                  ? windowLabels.days
                  : windowLabels.weeks}
            </option>
          ))}
        </select>
        {formWindowSeconds > 2592000 && (
          <span
            style={{
              fontSize: 11,
              color: 'var(--streamlain-error-color, #ef4444)',
            }}
          >
            {t['com.streamlain.settings.workspace.ai.budget.max-window']()}
          </span>
        )}
      </div>

      <div style={fieldRowStyle}>
        <span style={fieldLabelStyle}>
          {t['com.streamlain.settings.workspace.ai.budget.on-limit']()}
        </span>
        <select
          value={formBehavior}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            setFormBehavior(e.target.value)
          }
          style={selectStyle}
        >
          <option value="block">
            {t['com.streamlain.settings.workspace.ai.budget.block']()}
          </option>
          <option value="warn">
            {t['com.streamlain.settings.workspace.ai.budget.warn']()}
          </option>
        </select>
      </div>

      <div style={actionRowStyle}>
        <Button
          variant="secondary"
          onClick={() => {
            setEditingGroupId(null);
            setShowAddForm(false);
          }}
        >
          {t['com.streamlain.settings.workspace.ai.budget.cancel']()}
        </Button>
        <Button
          variant="primary"
          disabled={formWindowSeconds > 2592000}
          onClick={() =>
            void saveGroupLimit(showAddForm ? formGroupId : targetGroupId)
          }
        >
          {t['com.streamlain.settings.workspace.ai.budget.save']()}
        </Button>
      </div>
    </div>
  );

  // ─── Configured Limit Card ──────────────────────────────────────────────

  const renderLimitCard = (gl: GroupLimit) => (
    <div key={gl.groupId} style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div>
          <div style={cardTitleStyle}>{gl.groupName}</div>
          <div style={cardSubtitleStyle}>
            {formatCredits(
              gl.userLimitMicro ?? gl.totalLimitMicro,
              unlimitedLabel
            )}{' '}
            {t['com.streamlain.settings.workspace.ai.budget.credits']()}{' '}
            {formatWindow(gl.windowSeconds, windowLabels)}
          </div>
          <div style={cardSubtitleStyle}>
            {t['com.streamlain.settings.workspace.ai.budget.per-member']()}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={gl.behavior === 'block' ? badgeBlockStyle : badgeWarnStyle}
          >
            {gl.behavior === 'block'
              ? t['com.streamlain.settings.workspace.ai.budget.blocked']()
              : t['com.streamlain.settings.workspace.ai.budget.warning']()}
          </span>
          {isAdmin && (
            <>
              <Button variant="plain" onClick={() => startEdit(gl)}>
                {t['com.streamlain.settings.workspace.ai.budget.edit']()}
              </Button>
              <Button
                variant="plain"
                onClick={() => void deleteGroupLimit(gl.groupId)}
              >
                {t['com.streamlain.settings.workspace.ai.remove']()}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderOverrideForm = () => (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div>
          <div style={cardTitleStyle}>
            {t[
              'com.streamlain.settings.workspace.ai.budget.individual-override'
            ]()}
          </div>
          <div style={cardSubtitleStyle}>
            {t[
              'com.streamlain.settings.workspace.ai.budget.individual-override-desc'
            ]()}
          </div>
        </div>
      </div>

      <div style={fieldRowStyle}>
        <span style={fieldLabelStyle}>
          {t['com.streamlain.settings.workspace.ai.budget.type']()}
        </span>
        <select
          value={overrideEntityType}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => {
            setOverrideEntityType(e.target.value as OverrideEntityType);
            setOverrideEntityId('');
          }}
          style={selectStyle}
        >
          <option value="user">
            {t['com.streamlain.settings.workspace.ai.budget.user']()}
          </option>
          <option value="automation">
            {t['com.streamlain.settings.workspace.ai.budget.automation']()}
          </option>
          <option value="workflow">
            {t['com.streamlain.settings.workspace.ai.budget.workflow']()}
          </option>
        </select>
      </div>

      <div style={fieldRowStyle}>
        <span style={fieldLabelStyle}>
          {t['com.streamlain.settings.workspace.ai.budget.target']()}
        </span>
        <select
          value={overrideEntityId}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            setOverrideEntityId(e.target.value)
          }
          style={{ ...selectStyle, flex: 1 }}
        >
          {overrideOptions.map(option => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div style={fieldRowStyle}>
        <span style={fieldLabelStyle}>
          {t['com.streamlain.settings.workspace.ai.budget.budget']()}
        </span>
        <Input
          type="number"
          value={overrideCredits}
          onChange={(v: string) => setOverrideCredits(v)}
          placeholder={unlimitedLabel}
          style={{ flex: 1 }}
        />
        <span
          style={{
            fontSize: 13,
            color: 'var(--streamlain-text-secondary-color)',
          }}
        >
          {t['com.streamlain.settings.workspace.ai.budget.credits']()}
        </span>
      </div>

      <div style={fieldRowStyle}>
        <span style={fieldLabelStyle}>
          {t['com.streamlain.settings.workspace.ai.budget.window']()}
        </span>
        <Input
          type="number"
          value={String(overrideWindowAmount)}
          onChange={(v: string) =>
            setOverrideWindowAmount(Math.max(1, parseInt(v) || 1))
          }
          style={{ width: 64 }}
        />
        <select
          value={overrideWindowUnit}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            setOverrideWindowUnit(Number(e.target.value))
          }
          style={selectStyle}
        >
          {UNITS.map(u => (
            <option key={u.seconds} value={u.seconds}>
              {u.seconds === 3600
                ? windowLabels.hours
                : u.seconds === 86400
                  ? windowLabels.days
                  : windowLabels.weeks}
            </option>
          ))}
        </select>
        {overrideWindowSeconds > 2592000 && (
          <span
            style={{
              fontSize: 11,
              color: 'var(--streamlain-error-color, #ef4444)',
            }}
          >
            {t['com.streamlain.settings.workspace.ai.budget.max-window']()}
          </span>
        )}
      </div>

      <div style={fieldRowStyle}>
        <span style={fieldLabelStyle}>
          {t['com.streamlain.settings.workspace.ai.budget.on-limit']()}
        </span>
        <select
          value={overrideBehavior}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            setOverrideBehavior(e.target.value)
          }
          style={selectStyle}
        >
          <option value="block">
            {t['com.streamlain.settings.workspace.ai.budget.block']()}
          </option>
          <option value="warn">
            {t['com.streamlain.settings.workspace.ai.budget.warn']()}
          </option>
        </select>
      </div>

      <div style={actionRowStyle}>
        <Button
          variant="secondary"
          onClick={() => setShowAddOverrideForm(false)}
        >
          {t['com.streamlain.settings.workspace.ai.budget.cancel']()}
        </Button>
        <Button
          variant="primary"
          disabled={!overrideEntityId || overrideWindowSeconds > 2592000}
          onClick={() => void saveOverride()}
        >
          {t['com.streamlain.settings.workspace.ai.budget.save']()}
        </Button>
      </div>
    </div>
  );

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {data.groupLimits.length === 0 &&
          data.overrides.length === 0 &&
          !showAddForm && (
            <div style={emptyStateStyle}>
              {t['com.streamlain.settings.workspace.ai.no-budget-limits']()}
            </div>
          )}

        {showAddForm && renderForm('')}

        {data.groupLimits.map(gl =>
          editingGroupId === gl.groupId ? (
            <div key={gl.groupId}>{renderForm(gl.groupId)}</div>
          ) : (
            renderLimitCard(gl)
          )
        )}

        {showAddOverrideForm && renderOverrideForm()}

        {data.overrides.length > 0 && (
          <>
            <div
              style={{ ...cardSubtitleStyle, fontWeight: 500, paddingTop: 4 }}
            >
              {t['com.streamlain.settings.workspace.ai.individual-overrides']()}
            </div>
            {data.overrides.map(o => (
              <SettingRow
                key={o.id}
                name={`${o.entityType}: ${
                  overrideLabelByKey.get(`${o.entityType}:${o.entityId}`) ??
                  `${o.entityId.slice(0, 8)}…`
                }`}
                desc={`${formatCredits(o.limitMicro, unlimitedLabel)} ${t[
                  'com.streamlain.settings.workspace.ai.budget.credits'
                ]()} ${formatWindow(o.windowSeconds, windowLabels)}`}
              >
                {isAdmin && (
                  <Button
                    variant="plain"
                    onClick={() => void deleteOverride(o.id)}
                  >
                    {t['com.streamlain.settings.workspace.ai.remove']()}
                  </Button>
                )}
              </SettingRow>
            ))}
          </>
        )}

        {isAdmin &&
          hasOverrideTargets &&
          !showAddForm &&
          editingGroupId === null &&
          !showAddOverrideForm && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                paddingTop: 4,
              }}
            >
              <Button variant="secondary" onClick={startAddOverride}>
                {t[
                  'com.streamlain.settings.workspace.ai.budget.add-individual-override'
                ]()}
              </Button>
            </div>
          )}

        {!showAddForm &&
          editingGroupId === null &&
          isAdmin &&
          availableGroups.length > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                paddingTop: 4,
              }}
            >
              <Button variant="primary" onClick={startAdd}>
                {t['com.streamlain.settings.workspace.ai.add-limit']()}
              </Button>
            </div>
          )}
      </div>
    </>
  );
};
